import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Loader2, Search } from 'lucide-react';
import { useUI, useActions } from '../../context/AppContext';
import Modal from '../ui/Modal';
import { COMMON_INPUT_CLASS } from '../../utils/constants';
import { formatDateTR, getDayFromDate, getNextISODateForDay } from '../../utils/dateUtils';
import { matchesStudentSearch } from '../../utils/stringUtils';
import { sortStudentsByName } from '../../utils/studentLogic';

const HizliGorevModal = ({ isOpen, onClose, myStudents, students, showToast }) => {
  const { selectedDayForProgram, selectedDateISOForProgram, setSelectedDateISOForProgram } =
    useUI();
  const { createProgram } = useActions();
  const [tarih, setTarih] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentId, setStudentId] = useState('');
  const gun = getDayFromDate(tarih);

  const sortedStudents = useMemo(() => sortStudentsByName(myStudents), [myStudents]);
  /** Seçili öğrenci aramaya uymasa da listede kalır; yoksa seçim sessizce kaybolurdu */
  const studentOptions = useMemo(
    () => sortedStudents.filter((s) =>
      s.id === studentId || matchesStudentSearch([s.name, s.userCode], studentSearch)),
    [sortedStudents, studentSearch, studentId]
  );

  const handleStudentSearch = (value) => {
    setStudentSearch(value);
    const matches = sortedStudents.filter((s) => matchesStudentSearch([s.name, s.userCode], value));
    if (value.trim() && matches.length === 1) setStudentId(matches[0].id);
  };

  useEffect(() => {
    if (!isOpen) return;
    if (selectedDateISOForProgram) {
      setTarih(selectedDateISOForProgram);
    } else if (selectedDayForProgram) {
      setTarih(getNextISODateForDay(selectedDayForProgram));
    }
  }, [isOpen, selectedDateISOForProgram, selectedDayForProgram]);

  const handleClose = () => {
    setTarih('');
    setStudentSearch('');
    setStudentId('');
    setSelectedDateISOForProgram(null);
    onClose();
  };

  return (
    <Modal title="Hızlı Görev Ekle" isOpen={isOpen} onClose={handleClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (isSubmitting) return;
          setIsSubmitting(true);
          try {
            const targetStudent = students.find((s) => s.id === studentId);
            if (!targetStudent) return;

            await createProgram(targetStudent, {
              dateISO: tarih,
              warmup: e.target.warmup.value,
              main: e.target.main.value,
              cooldown: e.target.cooldown.value,
            });
            handleClose();
            showToast('Görev eklendi.');
          } catch (err) {
            showToast(err.message || 'Görev eklenemedi.', 'error');
          } finally {
            setIsSubmitting(false);
          }
        }}
        className="space-y-4"
      >
        {tarih && (
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-xl border border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
            Seçilen gün: <strong>{gun}</strong>
            <span className="text-blue-600/80 dark:text-blue-400/80"> ({formatDateTR(tarih)})</span>
          </div>
        )}
        <div>
          <label htmlFor="hizli-gorev-ogrenci" className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Öğrencilerim</label>
          {sortedStudents.length > 8 && (
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="search"
                value={studentSearch}
                onChange={(e) => handleStudentSearch(e.target.value)}
                placeholder="Ad veya numara ile ara"
                aria-label="Öğrenci ara"
                className={`${COMMON_INPUT_CLASS} pl-9`}
              />
            </div>
          )}
          <select
            id="hizli-gorev-ogrenci"
            name="studentId"
            required
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            className={COMMON_INPUT_CLASS}
          >
            <option value="">
              {studentSearch.trim() && studentOptions.length === 0 ? 'Eşleşen öğrenci yok' : 'Öğrenci Seçiniz...'}
            </option>
            {studentOptions.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-1 block">Tarih</label>
          <input type="date" required value={tarih} onChange={(e) => setTarih(e.target.value)} className={COMMON_INPUT_CLASS} />
          {gun && (
            <p className="mt-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
              {gun}
            </p>
          )}
        </div>
        <div>
          <label className="text-xs font-bold text-orange-500 uppercase">Isınma Seti</label>
          <input name="warmup" required placeholder="Örn: 500m Serbest Stil" className={COMMON_INPUT_CLASS} />
        </div>
        <div>
          <label className="text-xs font-bold text-blue-500 uppercase">Ana Set</label>
          <textarea name="main" required placeholder="Örn: 10x100m Tempo" className={`${COMMON_INPUT_CLASS} h-20 resize-none`} />
        </div>
        <div>
          <label className="text-xs font-bold text-emerald-500 uppercase">Soğuma Seti</label>
          <input name="cooldown" required placeholder="Örn: 200m Gevşeme" className={COMMON_INPUT_CLASS} />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-slate-900 dark:bg-blue-600 text-white py-4 rounded-xl font-bold mt-4 hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Ekleniyor...
            </>
          ) : (
            <>
              <Plus size={18} />
              Görevi Ekle
            </>
          )}
        </button>
      </form>
    </Modal>
  );
};

export default HizliGorevModal;
